import i18n from "i18next";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CrownIcon } from "../../../../assets/images/icons/CrownIcon";
import { FEATURE_FLAGS } from "../../../../constants/FeatureFlags.ts";
import RouterConstants from "../../../../constants/RouterConstants.ts";
import {
	IMAGE_REFERENCE_CONFIGS,
	IMAGE_REFERENCE_DISABLED_MODELS,
} from "../../../../constants/TextToImageConstants.ts";
import { useLdFeatureFlag } from "../../../../hooks/useLdFeatureFlag.ts";
import { useAppStore } from "../../../../hooks/useStores";
import { OrgFeatures } from "../../../../models/billing.ts";
import iframeStore from "../../../../pages/IframeNew/iframe-store.tsx";
import { TgModel } from "../../../../pages/NewTG/Models/store/tg-model-store.tsx";
import { isBriaMember, isTextToVector } from "../../../../utils";
import TGDropdown, { TGDropDownItem, TGDropDownProps } from "../TGDropDown/TGDropDown.tsx";
import styles from "./NewTextToImageModelsDropdown.module.scss";

type TextToImageModelsDropdownProps = {
	onFoundationModelsSuccess?: (foundationModels: TGDropDownItem[]) => void;
	onTailoredModelsSuccess?: (tailoredModels: TGDropDownItem[]) => void;
	onFoundationModelsChange?: (selectedOption: TGDropDownItem) => void;
	onTailoredModelsChange?: (selectedOption: TGDropDownItem) => void;
	isVector?: boolean;
	showFoundationModels?: boolean;
};

const TextToImageModelsDropdown = ({
	onFoundationModelsChange,
	onTailoredModelsChange,
	onFoundationModelsSuccess,
	onTailoredModelsSuccess,
	isVector = isTextToVector(),
	showFoundationModels = true,
	...rest
}: Partial<TGDropDownProps<string | string[]>> & TextToImageModelsDropdownProps) => {
	const { t } = useTranslation("translation", {
		keyPrefix: "playground.textToImage.config.models",
	});
	const { textToImageStore, tgStore, authStore } = useAppStore();
	const [foundationModelsItems, setFoundationModelsItems] = useState<TGDropDownItem[]>([]);
	const [tailoredModelsItems, setTailoredModelsItems] = useState<TGDropDownItem[]>([]);
	const [searchValue, setSearchValue] = useState<string>("");
	const isFiboLiteEnabled = useLdFeatureFlag(FEATURE_FLAGS.FIBO_LITE_FEATURE);

	useEffect(() => {
		const fetchModels = async () => {
			const foundationItems = showFoundationModels ? await createFoundationItems() : [];
			let filteredItems = foundationItems;

			if (isVector && showFoundationModels) {
				filteredItems = foundationItems.filter(
					(item) => item.value !== "hd_2.2" && item.value !== "fibo" && item.value !== "fibo_lite",
				);
			}

			setFoundationModelsItems(filteredItems);
			onFoundationModelsSuccess?.(foundationItems);

			const tailoredItems = await createTailoredItems();
			setTailoredModelsItems(tailoredItems);
			onTailoredModelsSuccess?.(tailoredItems);
		};
		if (!authStore.isLoadingOrgSubscriptions) {
			fetchModels();
		}
	}, [textToImageStore.imageReference, authStore.isLoadingOrgSubscriptions, isFiboLiteEnabled]);

	useEffect(() => {
		if (Object.keys(textToImageStore.models).length) {
			if (
				(!textToImageStore.config.model || !textToImageStore.config.model_version) &&
				!textToImageStore.config.tailored_model_id
			) {
				if (iframeStore.isIframe()) {
					textToImageStore.handleConfigChange("model", "fast");
					textToImageStore.handleConfigChange("model_version", "2.3");
				}
			}
		}
	}, [textToImageStore.models]);

	useEffect(() => {
		if (foundationModelsItems.length > 0) {
			textToImageStore.config = {
				...textToImageStore.config,
				model: isTextToVector() ? "base" : "fibo",
				model_version: isTextToVector() ? "3.2" : "",
				model_id: getModelKey(isTextToVector() ? "base" : "fibo", isTextToVector() ? "3.2" : ""),
				model_name: getModelDefaultLabel(isTextToVector() ? "base" : "fibo", isTextToVector() ? "3.2" : ""),
				fast: false,
				steps_num: IMAGE_REFERENCE_CONFIGS.steps[isTextToVector() ? "not_fast" : "fibo"].default,
			};
		}
	}, [foundationModelsItems.length]);

	const getModelDefaultLabel = (model: string, version: string) => {
		const formattedVersion =
			model === "fibo" && version ? version.charAt(0).toUpperCase() + version.slice(1).toLowerCase() : version;
		return `${i18n.t(`playground.textToImage.config.models.dropdownLabels.${model}`, {
			version: formattedVersion,
		})}`;
	};

	const handleFoundationChange = (item: TGDropDownItem) => {
		if (!item) return;
		const selectedOption = foundationModelsItems.find((option) => option?.value === item.value);
		if (!selectedOption) return;

		const model = getModelName(item.value as string);
		const modelVersion = getModelVersion(item.value as string);
		if (
			textToImageStore.isImageReferenceEnabled() &&
			IMAGE_REFERENCE_DISABLED_MODELS.includes(getModelKey(model, modelVersion) as ModelDropdownKey) &&
			textToImageStore.imageReference.url
		) {
			textToImageStore.handleDeleteImageReference();
		}
		onFoundationModelsChange?.(selectedOption);
	};

	const handleTailoredChange = (item: TGDropDownItem) => {
		const selectedOption = tailoredModelsItems.find((option) => option?.value === item.value);
		if (!selectedOption) return;

		if (authStore.isFeatureEnabled(OrgFeatures.TAILORED_GENERATION) || isBriaMember(authStore.user)) {
			onTailoredModelsChange?.(selectedOption);
		} else {
			window.open(`${RouterConstants.CONSOLE.fullPath}?pricing=true`);
		}
	};

	const createFoundationItems = async (): Promise<TGDropDownItem[]> => {
		const models = await textToImageStore.getModels();

		const items = Object.entries(models)
			.flatMap(([modelName, model]) => {
				const versions = model.versions.filter((version: string) =>
					modelName === "fibo" && !isFiboLiteEnabled ? version !== "lite" : true,
				);

				return versions.map((version: string) => ({
					id: getModelKey(modelName, version),
					key:
						iframeStore?.iframe?.config.image_generation_config?.enabled_foundation_models?.[modelName]?.[
							version
						] ?? getModelDefaultLabel(modelName, version),
					value: getModelKey(modelName, version),
					label:
						iframeStore?.iframe?.config.image_generation_config?.enabled_foundation_models?.[modelName]?.[
							version
						] ?? getModelDefaultLabel(modelName, version),
				}));
			})
			.sort(customfoundationModelsSort);

		return items.length === 0
			? [
					{
						id: "fibo",
						key: getModelDefaultLabel("fibo", ""),
						value: "fibo",
						label: getModelDefaultLabel("fibo", ""),
					},
					{
						id: "fibo_lite",
						key: getModelDefaultLabel("fibo", "lite"),
						value: "fibo_lite",
						label: getModelDefaultLabel("fibo", "lite"),
					},
				]
			: items;
	};

	const createTailoredItems = async (): Promise<TGDropDownItem[]> => {
		const createItem = (model: TgModel): TGDropDownItem => ({
			value: model.id.toString(),
			key: model.name ?? "",
			description: model.description ?? "",
			extraData: {
				isTailoredModel: true,
				model_id: model.id.toString() ?? "",
				training_version: model.training_version,
				generation_prefix: model.generation_prefix,
			},
			badge: model.training_version,
			badgeClassName: styles.badge,
			icon:
				!isBriaMember(authStore.user) && !authStore.isFeatureEnabled(OrgFeatures.TAILORED_GENERATION) ? (
					<CrownIcon />
				) : (
					""
				),
		});

		try {
			if (tgStore.tgModelStore.modelsList?.length) {
				return tgStore.tgModelStore.modelsList
					.filter((model) => model.training_version === "fibo")
					.map((model) => createItem(model));
			}

			const loadedModels = await tgStore.tgModelStore.listAllModels("Completed");
			if (!loadedModels) return [];

			return loadedModels.filter((model) => model.training_version === "fibo").map((model) => createItem(model));
		} catch (error) {
			console.error("Error loading tailored models:", error);
			return [];
		}
	};

	function customfoundationModelsSort(item1: TGDropDownItem, item2: TGDropDownItem) {
		const order: ModelDropdownKey[] = [
			"fibo",
			"fibo_lite",
			"base_3.2",
			"base_3.1",
			"fast_2.3",
			"fast_2.2",
			"hd_2.2",
			"tailored_models",
		];
		return order.indexOf(item1.value) - order.indexOf(item2.value);
	}

	const handleSearchChange = (searchText: string) => {
		setSearchValue(searchText);
	};

	return (
		<TGDropdown
			foundationModels={[
				...foundationModelsItems,
				{
					key: "Tailored Engines",
					value: "TailoredModels",
					id: "TailoredModels",
					label: "Tailored Engines",
					className: styles.tailoredItems,
				},
			]}
			tailoredModels={tailoredModelsItems.filter((item) =>
				item.key.toLowerCase().trim().includes(searchValue.toLowerCase().trim()),
			)}
			hideTG={iframeStore.isIframe() && !tailoredModelsItems.length}
			onTailoredModelsChange={handleTailoredChange}
			onFoundationModelsChange={handleFoundationChange}
			menuMaxHeight="50vh"
			className={styles.foundationModelsMenu}
			placeholder={t("placeholder")}
			searchable={true}
			searchIcon={true}
			debounceSearch={true}
			SearchPlaceholder={t("dropdownLabels.search")}
			onSearchChange={(e) => {
				e.preventDefault();
				e.stopPropagation();
				handleSearchChange(e.target.value);
			}}
			onClose={() => {
				setSearchValue("");
			}}
			{...rest}
		/>
	);
};

export default observer(TextToImageModelsDropdown);

export const getModelKey = (model: string, version: string) => {
	return `${model}${version ? `_${version}` : ""}`;
};

export const getModelName = (modelKey: string) => {
	return modelKey.split("_")[0];
};

export const getModelVersion = (modelKey: string) => {
	return modelKey.split("_")[1];
};

export type ModelDropdownKey =
	"base_3.2" | "base_3.1" | "fast_2.3" | "fast_2.2" | "hd_2.2" | "tailored_models" | "fibo" | "fibo_lite";
